// hooks/useDebounce.ts
import { useEffect, useMemo, useRef } from 'react';

/**
 * Returns a debounced version of `callback` that delays invoking it until
 * `delay` milliseconds have passed since the last call. The returned function
 * keeps a stable identity across renders, and any pending call is cancelled
 * on unmount.
 *
 * @param callback The function to debounce.
 * @param delay Delay in milliseconds (defaults to 500ms).
 */
const useDebounce = <Args extends unknown[]>(
  callback: (...args: Args) => void,
  delay: number = 500
) => {
  const timer = useRef<ReturnType<typeof setTimeout>>();

  // Keep the latest callback without changing the debounced function's identity.
  const callbackRef = useRef(callback);
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debounced = useMemo(() => {
    const fn = (...args: Args) => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    };

    fn.cancel = () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };

    return fn;
  }, [delay]);

  // Cancel any pending invocation on unmount.
  useEffect(() => () => debounced.cancel(), [debounced]);

  return debounced;
};

export default useDebounce;
